// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
import React, { useEffect, useState } from 'react';
import pt from 'prop-types';

import { toString as _toString } from 'lodash';

import BaseTable from '../../base-table/BaseTable';
import { MrInputNumber, MrInputText, MrInputTextarea, MrText, MrToggleText, MrWithTooltip } from '@ion/components';
import { hydrateToRequest } from './hydrateToRequest';
import { hydrateFromDB } from './hydrateFromDB';
import { BRING_YOUR_OWN_SYNCS } from './consts';
import { Button, PlusCircleIcon, Trash02Icon } from '@metarouter-private/mr-mui';

const BringYourOwnSyncFieldsTable = ({
  syncName,
  formState,
  dataFields,
  requestInputName,
  validateRequired,
  resetError,
  setField,
}) => {
  const inputTypes = {
    NUMBER: MrInputNumber,
    TEXT_AREA: MrInputTextarea,
    default: MrInputText,
  };

  const inputName = `sync-${syncName}-${requestInputName}`;

  const [data, setData] = useState();
  const [haveInitFormState, setHaveInitFormState] = useState(false);
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});

  useEffect(() => {
    if (haveInitFormState) return;
    const dbData = formState[inputName]?.value;
    const initialData = hydrateFromDB(dbData, syncName);
    setData(initialData);
    setHaveInitFormState(true);
  }, [formState, syncName, inputName, haveInitFormState]);

  const syncToFormState = updatedData => {
    const hydratedValue = hydrateToRequest(syncName, updatedData);
    setField({
      target: {
        name: inputName,
        value: hydratedValue,
      },
    });
  };

  const updateData = updatedData => {
    setData(updatedData);
    syncToFormState(updatedData);
  };

  const createFieldSet = dataFields => {
    return dataFields.reduce((acc, field) => {
      acc[field.property] = field.default;
      return acc;
    }, {});
  };

  const generateID = data => {
    const lastElement = data[data.length - 1];
    return lastElement.id + 1;
  };

  const removeRow = id => {
    updateData(data.filter(row => row.id !== id));
  };

  const handleChange = (id, property, value) => {
    const updates = { [property]: value };
    if (value === false) {
      const keysToRemove = [];
      dataFields
        .filter(dependentField => dependentField.dependsOn === property)
        .forEach(dependentField => {
          updates[dependentField.property] = dependentField.default;
          keysToRemove.push(`${dependentField.property}-${id}`);
        });
      if (keysToRemove.length > 0) {
        setFieldValidationErrors(prev => {
          const next = { ...prev };
          keysToRemove.forEach(key => {
            delete next[key];
          });
          return next;
        });
      }
    }
    updateData(data.map(row => (row.id === id ? { ...row, ...updates } : row)));
  };

  const addRow = e => {
    e.preventDefault();
    const id = generateID(data);
    const newFields = createFieldSet(dataFields);
    updateData([...data, { id: id, ...newFields }]);
  };

  const renderFieldValue = (event, row, field) => {
    event.preventDefault();
    handleChange(row.id, field.property, event.target.value);
    if (field.validate) {
      const error = field.validate(event.target.value);
      setFieldValidationErrors(prev => ({
        ...prev,
        [`${field.property}-${row.id}`]: error,
      }));
    }
  };

  const onRemoveClick = (e, row) => {
    e.preventDefault();
    removeRow(row.id);
  };

  const isLastItem = (array, item) => array.lastIndexOf(item) === array.length - 1;

  return data ? (
    <>
      <MrText>{BRING_YOUR_OWN_SYNCS[syncName]?.DESCRIPTION || ''}</MrText>
      {data?.map((row, rowIndex) => (
        <BaseTable styleNames="greyTable" key={`${requestInputName}-${row.id}`}>
          <tbody>
            {dataFields.map(field => {
              if (field.dependsOn && !row[field.dependsOn]) return null;
              const InputComponent = inputTypes[field.type] || inputTypes.default;
              const fieldName = `${inputName}-${field.property}-${rowIndex}`;
              return (
                <tr key={`${requestInputName}-${row.id}-${field.property}`}>
                  <td>
                    <MrWithTooltip message={field.description}>
                      <MrText styleNames="bold" component="span" required={field.required}>
                        {field.title}
                      </MrText>
                    </MrWithTooltip>
                  </td>
                  <td>
                    {field.type === 'BOOL' ? (
                      <MrToggleText
                        name={fieldName}
                        value={_toString(row[field.property] ?? field.default)}
                        onChange={e => handleChange(row.id, field.property, e.target.value === 'true')}
                      />
                    ) : (
                      <InputComponent
                        placeholder={field.placeholder}
                        onChange={event => renderFieldValue(event, row, field)}
                        value={row[field.property]}
                        onInput={resetError}
                        name={fieldName}
                        errorMsg={
                          field.validate
                            ? fieldValidationErrors[`${field.property}-${row.id}`]
                            : formState[fieldName]?.errorMsg
                        }
                        validators={field.required ? [validateRequired()] : []}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                {data.length > 1 && (
                  <Button
                    onClick={e => onRemoveClick(e, row)}
                    variant="text"
                    color="secondary"
                    startIcon={<Trash02Icon />}
                  >
                    Remove
                  </Button>
                )}
              </td>
              <td>
                {isLastItem(data, row) && (
                  <Button color="secondary" onClick={addRow} variant="text" startIcon={<PlusCircleIcon />}>
                    Add
                  </Button>
                )}
              </td>
            </tr>
          </tbody>
        </BaseTable>
      ))}
      <input type="hidden" name={inputName} value={hydrateToRequest(syncName, data)} />
    </>
  ) : null;
};

BringYourOwnSyncFieldsTable.propTypes = {
  formState: pt.object.isRequired,
  validateRequired: pt.func.isRequired,
  resetError: pt.func.isRequired,
  syncName: pt.string.isRequired,
  requestInputName: pt.string.isRequired,
  dataFields: pt.array.isRequired,
  setField: pt.func.isRequired,
};
export default BringYourOwnSyncFieldsTable;
